/**
 * Syntax highlighting for the files view.
 *
 * Shiki is assembled by hand rather than taken as a bundle: the core, the raw
 * JavaScript engine (precompiled grammars, so no oniguruma wasm) and one
 * grammar per language, each behind a dynamic import. A phone that never opens
 * a Rust file never downloads the Rust grammar, and the service worker keeps
 * whatever it did fetch.
 */

import type { HighlighterCore, ThemedToken, ThemeRegistrationRaw } from '@shikijs/core';

/** A tokenized run inside one line: the text plus how it should be painted. */
export type CodeToken = {
  text: string;
  color?: string;
  italic?: boolean;
  bold?: boolean;
};

/**
 * Highlighting a quarter-megabyte file on a phone costs more than it is worth,
 * and the gateway caps a read at 256 KB anyway. Past this the view stays plain.
 */
export const MAX_HIGHLIGHT_BYTES = 120_000;

/**
 * A theme in the app's own palette, so code reads as part of the UI instead of
 * a slab of VS Code pasted into it. Blue carries the names the eye looks for
 * first (functions, keys), green is data, amber is a literal, grey is a comment.
 */
const THEME = {
  name: 'agent-night',
  type: 'dark',
  colors: { 'editor.background': '#101319', 'editor.foreground': '#dbe2ee' },
  settings: [
    { settings: { foreground: '#dbe2ee', background: '#101319' } },
    { scope: ['comment', 'punctuation.definition.comment'], settings: { foreground: '#5f6879', fontStyle: 'italic' } },
    { scope: ['string', 'string.quoted', 'meta.embedded.assembly'], settings: { foreground: '#86d8a4' } },
    { scope: ['constant.character.escape', 'constant.other.placeholder'], settings: { foreground: '#ffb454' } },
    { scope: ['constant.numeric', 'constant.language', 'keyword.other.unit'], settings: { foreground: '#ffb454' } },
    { scope: ['keyword', 'keyword.control', 'storage', 'storage.type', 'storage.modifier'], settings: { foreground: '#b8a0ff' } },
    { scope: ['keyword.operator'], settings: { foreground: '#93a0b8' } },
    { scope: ['entity.name.function', 'support.function', 'meta.function-call.generic'], settings: { foreground: '#7aa2ff' } },
    { scope: ['entity.name.type', 'entity.name.class', 'support.type', 'support.class', 'entity.other.inherited-class'], settings: { foreground: '#6fd3d0' } },
    { scope: ['variable', 'meta.definition.variable'], settings: { foreground: '#dbe2ee' } },
    { scope: ['variable.parameter', 'variable.other.readwrite.alias'], settings: { foreground: '#c8d0e0' } },
    { scope: ['variable.language', 'variable.other.constant', 'support.variable'], settings: { foreground: '#6fd3d0' } },
    { scope: ['punctuation', 'meta.brace'], settings: { foreground: '#8b93a4' } },
    { scope: ['entity.name.tag', 'support.type.property-name.css'], settings: { foreground: '#ff9e9e' } },
    { scope: ['entity.other.attribute-name', 'entity.name.tag.custom'], settings: { foreground: '#ffb454' } },
    { scope: ['support.type.property-name', 'meta.object-literal.key'], settings: { foreground: '#7aa2ff' } },
    { scope: ['string.regexp', 'constant.other.symbol'], settings: { foreground: '#6fd3d0' } },
    { scope: ['entity.name.section', 'markup.heading'], settings: { foreground: '#7aa2ff', fontStyle: 'bold' } },
    { scope: ['markup.bold'], settings: { fontStyle: 'bold' } },
    { scope: ['markup.italic'], settings: { fontStyle: 'italic' } },
    { scope: ['markup.inline.raw', 'markup.fenced_code', 'markup.raw'], settings: { foreground: '#86d8a4' } },
    { scope: ['markup.underline.link', 'string.other.link'], settings: { foreground: '#6fd3d0' } },
    { scope: ['markup.list', 'punctuation.definition.list'], settings: { foreground: '#ffb454' } },
    { scope: ['markup.quote'], settings: { foreground: '#8b93a4', fontStyle: 'italic' } },
    { scope: ['markup.inserted', 'meta.diff.header.to-file'], settings: { foreground: '#58d08b' } },
    { scope: ['markup.deleted', 'meta.diff.header.from-file'], settings: { foreground: '#ff6b6b' } },
    { scope: ['invalid', 'invalid.illegal'], settings: { foreground: '#ff6b6b' } },
  ],
} satisfies ThemeRegistrationRaw;

/** Every grammar the view can reach, one dynamic import each. */
const LOADERS = {
  bash: () => import('@shikijs/langs-precompiled/shellscript'),
  c: () => import('@shikijs/langs-precompiled/c'),
  cmake: () => import('@shikijs/langs-precompiled/cmake'),
  cpp: () => import('@shikijs/langs-precompiled/cpp'),
  csharp: () => import('@shikijs/langs-precompiled/csharp'),
  css: () => import('@shikijs/langs-precompiled/css'),
  diff: () => import('@shikijs/langs-precompiled/diff'),
  docker: () => import('@shikijs/langs-precompiled/docker'),
  go: () => import('@shikijs/langs-precompiled/go'),
  graphql: () => import('@shikijs/langs-precompiled/graphql'),
  hcl: () => import('@shikijs/langs-precompiled/hcl'),
  html: () => import('@shikijs/langs-precompiled/html'),
  ini: () => import('@shikijs/langs-precompiled/ini'),
  java: () => import('@shikijs/langs-precompiled/java'),
  javascript: () => import('@shikijs/langs-precompiled/javascript'),
  jsonc: () => import('@shikijs/langs-precompiled/jsonc'),
  jsx: () => import('@shikijs/langs-precompiled/jsx'),
  kotlin: () => import('@shikijs/langs-precompiled/kotlin'),
  lua: () => import('@shikijs/langs-precompiled/lua'),
  make: () => import('@shikijs/langs-precompiled/make'),
  markdown: () => import('@shikijs/langs-precompiled/markdown'),
  nginx: () => import('@shikijs/langs-precompiled/nginx'),
  php: () => import('@shikijs/langs-precompiled/php'),
  proto: () => import('@shikijs/langs-precompiled/proto'),
  python: () => import('@shikijs/langs-precompiled/python'),
  ruby: () => import('@shikijs/langs-precompiled/ruby'),
  rust: () => import('@shikijs/langs-precompiled/rust'),
  scss: () => import('@shikijs/langs-precompiled/scss'),
  sql: () => import('@shikijs/langs-precompiled/sql'),
  svelte: () => import('@shikijs/langs-precompiled/svelte'),
  swift: () => import('@shikijs/langs-precompiled/swift'),
  toml: () => import('@shikijs/langs-precompiled/toml'),
  tsx: () => import('@shikijs/langs-precompiled/tsx'),
  typescript: () => import('@shikijs/langs-precompiled/typescript'),
  vue: () => import('@shikijs/langs-precompiled/vue'),
  xml: () => import('@shikijs/langs-precompiled/xml'),
  yaml: () => import('@shikijs/langs-precompiled/yaml'),
} as const;

export type LangId = keyof typeof LOADERS;

/** Human name for the chip in the file header. */
const LABELS: Record<LangId, string> = {
  bash: 'Shell',
  c: 'C',
  cmake: 'CMake',
  cpp: 'C++',
  csharp: 'C#',
  css: 'CSS',
  diff: 'Diff',
  docker: 'Dockerfile',
  go: 'Go',
  graphql: 'GraphQL',
  hcl: 'HCL',
  html: 'HTML',
  ini: 'INI',
  java: 'Java',
  javascript: 'JavaScript',
  jsonc: 'JSON',
  jsx: 'JSX',
  kotlin: 'Kotlin',
  lua: 'Lua',
  make: 'Makefile',
  markdown: 'Markdown',
  nginx: 'nginx',
  php: 'PHP',
  proto: 'Protobuf',
  python: 'Python',
  ruby: 'Ruby',
  rust: 'Rust',
  scss: 'SCSS',
  sql: 'SQL',
  svelte: 'Svelte',
  swift: 'Swift',
  toml: 'TOML',
  tsx: 'TSX',
  typescript: 'TypeScript',
  vue: 'Vue',
  xml: 'XML',
  yaml: 'YAML',
};

const BY_EXTENSION: Record<string, LangId> = {
  bash: 'bash',
  c: 'c',
  cc: 'cpp',
  cjs: 'javascript',
  cmake: 'cmake',
  conf: 'ini',
  cpp: 'cpp',
  cs: 'csharp',
  css: 'css',
  cts: 'typescript',
  diff: 'diff',
  go: 'go',
  gql: 'graphql',
  graphql: 'graphql',
  h: 'c',
  hcl: 'hcl',
  hpp: 'cpp',
  htm: 'html',
  html: 'html',
  ini: 'ini',
  java: 'java',
  js: 'javascript',
  json: 'jsonc',
  json5: 'jsonc',
  jsonc: 'jsonc',
  jsx: 'jsx',
  kt: 'kotlin',
  kts: 'kotlin',
  lua: 'lua',
  markdown: 'markdown',
  md: 'markdown',
  mdx: 'markdown',
  mjs: 'javascript',
  mts: 'typescript',
  patch: 'diff',
  php: 'php',
  proto: 'proto',
  py: 'python',
  rb: 'ruby',
  rs: 'rust',
  sass: 'scss',
  scss: 'scss',
  sh: 'bash',
  sql: 'sql',
  svelte: 'svelte',
  svg: 'xml',
  swift: 'swift',
  tf: 'hcl',
  tfvars: 'hcl',
  toml: 'toml',
  ts: 'typescript',
  tsx: 'tsx',
  vue: 'vue',
  xml: 'xml',
  yaml: 'yaml',
  yml: 'yaml',
  zsh: 'bash',
};

/** Files whose name, not extension, says what they are. */
const BY_NAME: Record<string, LangId> = {
  '.bash_profile': 'bash',
  '.bashrc': 'bash',
  '.env': 'ini',
  '.gitconfig': 'ini',
  '.npmrc': 'ini',
  '.zshrc': 'bash',
  caddyfile: 'nginx',
  cmakelists: 'cmake',
  gnumakefile: 'make',
  makefile: 'make',
};

export function languageForPath(path: string): LangId | null {
  const name = (path.split('/').pop() ?? '').toLowerCase();
  const byName = BY_NAME[name];
  if (byName) return byName;
  if (name.startsWith('dockerfile')) return 'docker';
  if (name.startsWith('.env.')) return 'ini';
  const dot = name.lastIndexOf('.');
  if (dot <= 0) return null;
  return BY_EXTENSION[name.slice(dot + 1)] ?? null;
}

/** What the header chip says: the language, or the bare extension. */
export function languageLabel(path: string): string {
  const lang = languageForPath(path);
  if (lang) return LABELS[lang];
  const name = (path.split('/').pop() ?? '').toLowerCase();
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toUpperCase() : 'Text';
}

let core: Promise<HighlighterCore> | null = null;
let ready: HighlighterCore | null = null;
const loaded = new Set<LangId>();

/** The engine itself is a chunk too: the app shell does not pay for it. */
function highlighter(): Promise<HighlighterCore> {
  core ??= (async () => {
    const [{ createHighlighterCore }, { createJavaScriptRawEngine }] = await Promise.all([
      import('@shikijs/core'),
      import('@shikijs/engine-javascript/raw'),
    ]);
    ready = await createHighlighterCore({
      themes: [THEME],
      langs: [],
      engine: createJavaScriptRawEngine(),
    });
    return ready;
  })();
  return core;
}

/**
 * Pull down the engine and one grammar. Resolves either way: a chunk that will
 * not load on a flaky connection leaves the file plain, which is still
 * readable, so nothing here is worth throwing over.
 */
export async function loadLanguage(lang: LangId): Promise<void> {
  try {
    const shiki = await highlighter();
    if (loaded.has(lang)) return;
    shiki.loadLanguageSync((await LOADERS[lang]()).default);
    loaded.add(lang);
  } catch {
    /* plain lines are the fallback everywhere */
  }
}

/**
 * Tokenize without waiting, or null when the engine or the grammar is not in
 * memory yet. The editor repaints under a live caret and cannot afford a frame
 * where the colour lags a keystroke; call loadLanguage first, then this.
 */
export function highlightLinesSync(text: string, lang: LangId): CodeToken[][] | null {
  if (ready === null || !loaded.has(lang)) return null;
  try {
    return toLines(ready.codeToTokens(text, { lang, theme: THEME.name }).tokens);
  } catch {
    return null;
  }
}

/** Tokenize into lines, falling back to plain text if anything goes wrong. */
export async function highlightLines(text: string, lang: LangId): Promise<CodeToken[][]> {
  await loadLanguage(lang);
  return highlightLinesSync(text, lang) ?? plainLines(text);
}

function toLines(tokens: ThemedToken[][]): CodeToken[][] {
  return tokens.map((line) =>
    line.map((token) => ({
      text: token.content,
      color: token.color,
      italic: ((token.fontStyle ?? 0) & 1) !== 0,
      bold: ((token.fontStyle ?? 0) & 2) !== 0,
    })),
  );
}

export function plainLines(text: string): CodeToken[][] {
  return text.split('\n').map((line) => [{ text: line }]);
}
